import { useEffect, useState } from "react"
import { createFileRoute } from "@tanstack/react-router"

/**
 * Public album page: anyone with the signed link sees the pictures until
 * it expires. No account, no app shell — just the grid and a lightbox.
 */
export const Route = createFileRoute("/share/album")({
  component: SharedAlbum,
  validateSearch: (s: Record<string, unknown>): { t?: string } => ({ t: typeof s.t === "string" ? s.t : undefined }),
})

type Item = { id: string; kind?: string; width: number | null; height: number | null; url: string; thumb_url: string }
type Album = { name: string; count: number; items: Item[] }

function SharedAlbum() {
  const { t } = Route.useSearch()
  const [album, setAlbum] = useState<Album | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [open, setOpen] = useState<number | null>(null)
  const el = (navigator.language || "").toLowerCase().startsWith("el")

  useEffect(() => {
    if (!t) { setErr(el ? "Λείπει ο σύνδεσμος." : "Missing link."); return }
    fetch(`/api/public/album?t=${encodeURIComponent(t)}`).then(async (r) => {
      if (!r.ok) throw new Error(r.status === 403 ? (el ? "Ο σύνδεσμος έληξε ή δεν ισχύει." : "This link has expired or is invalid.") : `${r.status}`)
      setAlbum(await r.json())
    }).catch((e) => setErr((e as Error).message))
  }, [t, el])

  useEffect(() => {
    if (open === null || !album) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(null)
      else if (e.key === "ArrowLeft") setOpen((i) => (i === null ? i : Math.max(0, i - 1)))
      else if (e.key === "ArrowRight") setOpen((i) => (i === null ? i : Math.min(album.items.length - 1, i + 1)))
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, album])

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="flex items-center gap-3 border-b border-border px-5 py-4 md:px-8">
        <img src="/lucy-logo.png" alt="" className="h-8 w-8 rounded-full" />
        <span className="font-display text-[18px]">Lucy</span>
        {album && (
          <span className="ml-3 truncate text-[15px] text-foreground/90">
            {album.name} <span className="font-mono text-[11px] text-muted-foreground">· {album.count}</span>
          </span>
        )}
      </header>
      <main className="p-4 md:p-8">
        {err && <div className="rounded-2xl bg-card p-6 text-sm text-muted-foreground ring-1 ring-inset ring-border">{err}</div>}
        {!err && !album && <div className="text-sm text-muted-foreground">…</div>}
        {album && album.items.length === 0 && <div className="text-sm text-muted-foreground">{el ? "Το άλμπουμ είναι άδειο." : "This album is empty."}</div>}
        {album && album.items.length > 0 && (
          <div className="columns-2 gap-3 sm:columns-3 lg:columns-4 xl:columns-5 [&>*]:mb-3 [&>*]:break-inside-avoid">
            {album.items.map((it, i) => (
              <button key={it.id} type="button" onClick={() => setOpen(i)} className="block w-full overflow-hidden rounded-xl bg-card ring-1 ring-inset ring-border">
                <img src={it.thumb_url} alt="" loading="lazy" className="block w-full" />
              </button>
            ))}
          </div>
        )}
      </main>
      {album && open !== null && album.items[open] && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4" onClick={() => setOpen(null)}>
          {album.items[open].kind === "video"
            ? <video src={album.items[open].url} controls autoPlay playsInline className="max-h-[92vh] max-w-full rounded-lg bg-black" onClick={(e) => e.stopPropagation()} />
            : <img src={album.items[open].url} alt="" className="max-h-[92vh] max-w-full rounded-lg object-contain" onClick={(e) => e.stopPropagation()} />}
          {open > 0 && <button type="button" aria-label="Previous" onClick={(e) => { e.stopPropagation(); setOpen(open - 1) }} className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-card/80 text-foreground ring-1 ring-inset ring-border">‹</button>}
          {open < album.items.length - 1 && <button type="button" aria-label="Next" onClick={(e) => { e.stopPropagation(); setOpen(open + 1) }} className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-card/80 text-foreground ring-1 ring-inset ring-border">›</button>}
          <span className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-card/80 px-2.5 py-1 font-mono text-[11px] text-muted-foreground ring-1 ring-inset ring-border">{open + 1} / {album.items.length}</span>
        </div>
      )}
    </div>
  )
}
